import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { AccountabilityFooter } from '../components/AccountabilityFooter';
import { Storing, WeekZonderOverzicht } from '../components/MeasurementState';
import { PeriodPicker } from '../components/PeriodPicker';
import { ANDERE, API_SLEUTEL, soortVan, TeamOverzicht } from '../components/TeamOverzicht';
import { useMeldToestand } from '../components/Toestandsmelding';
import { Werksoortschakelaar } from '../components/Werksoortschakelaar';
import { FOCUSRING } from '../focus';
import { isObject } from '../format';
import { useGekozenWeek } from '../hooks/useGekozenWeek';
import { useGekozenWerksoort, WERKSOORT_LABEL, type Werksoort } from '../hooks/useGekozenWerksoort';
import { useMedewerkers } from '../hooks/useOverzicht';
import { STORING_ONBEREIKBAAR, vorigeWeekVan, weektoestand } from '../toestand';
import { weeknummer } from '../week';

const ANKER = /^#(medewerker-\d+)$/;

/**
 * Team — de startpagina van Werkkwaliteit voor wie het team mag zien (S1, variant A, CAP-1, besluit 31-10-2026).
 *
 * Weekkiezer en Werksoortschakelaar staan er in elke toestand; daaronder de eerste toestand die past: laden,
 * storing, geen overzicht (`data: null`), oudere meetmethode, niets nagekeken, of de gewone weergave
 * (`TeamOverzicht`). Het overzicht en het Nakijkers-seintje zijn twee antwoorden, maar het seintje telt alleen
 * velden van dezelfde week; een versieverschil verandert geen getal over een persoon, dus Team herlaadt daar
 * niet op (AD-24 blijft alleen op AI).
 */
export function AllEmployeesPage() {
  const { week, weken, kies } = useGekozenWeek();
  const { werksoort } = useGekozenWerksoort();
  const medewerkers = useMedewerkers(week);
  const vorigeWeek = vorigeWeekVan(weken, week);
  const { hash } = useLocation();
  const inhoudRef = useRef<HTMLDivElement>(null);
  // Na "Opnieuw proberen" dat lukt, gaat de focus naar de inhoudscontainer (die met de Teamzin begint); lukt
  // het niet, dan blijft hij op de melding.
  const herpoging = useRef(false);
  // Terug vanuit de medewerkerpagina: één keer per binnenkomend anker de focus op de link van die rij.
  const ankerGedaan = useRef<string | null>(null);

  const antwoord = medewerkers.data;
  const storing =
    medewerkers.isError || antwoord === undefined || (antwoord.data === null && weektoestand(antwoord.reden) !== 'nog_niet_gemeten');

  useEffect(() => {
    herpoging.current = false;
  }, [week]);

  useEffect(() => {
    if (!herpoging.current || medewerkers.isLoading || storing) return;
    herpoging.current = false;
    inhoudRef.current?.focus();
  });

  useEffect(() => {
    // Pas afgehandeld als er een overzicht met gegevens staat: na een storing en een geslaagde herpoging krijgt
    // de rij de focus alsnog.
    if (hash === '' || hash === ankerGedaan.current || medewerkers.isLoading || medewerkers.isError || !isObject(antwoord?.data)) return;
    ankerGedaan.current = hash;
    const id = ANKER.exec(hash)?.[1];
    if (id !== undefined) document.getElementById(id)?.querySelector<HTMLElement>('a')?.focus();
  });

  function opnieuw() {
    herpoging.current = true;
    medewerkers.refetch();
  }

  function inhoud() {
    if (weken.length === 0) return null;
    if (medewerkers.isLoading) return <TeamLaden />;
    if (medewerkers.isError || antwoord === undefined) {
      return <Storing onHerlaad={opnieuw} oorzaak={STORING_ONBEREIKBAAR} vorigeWeek={vorigeWeek} week={week} />;
    }
    if (antwoord.data === null) {
      return <WeekZonderOverzicht antwoord={antwoord} onHerlaad={opnieuw} vorigeWeek={vorigeWeek} week={week} weken={weken} />;
    }

    const overzicht = antwoord.data;
    const sleutel = API_SLEUTEL[werksoort];
    const rijen = Array.isArray(overzicht.medewerkers) ? overzicht.medewerkers.filter(isObject) : [];
    const voet = <AccountabilityFooter afbakening={overzicht.afbakening} weekrapport weekscherm />;
    // Alleen rijen mét deze werksoort tellen mee; minstens één moet er zijn.
    const metSoort = rijen.map((rij) => soortVan(rij, sleutel)).filter((soort) => soort !== undefined);
    const oud = metSoort.length > 0 && metSoort.every((soort) => soort.cijfer?.reden === 'niet_gemeten_in_deze_meetversie');
    if (oud) {
      return (
        <>
          <OudeMeetmethode />
          {voet}
        </>
      );
    }
    if (rijen.length === 0 || overzicht.team?.[sleutel]?.reden === 'niets_nagekeken') {
      return (
        <>
          <NietsNagekeken week={week} werksoort={werksoort} />
          {voet}
        </>
      );
    }
    return (
      <>
        <TeamOverzicht overzicht={overzicht} rijen={rijen} week={week} werksoort={werksoort} />
        {voet}
      </>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-5" data-testid="werkkwaliteit-alle-medewerkers">
      <div className="flex flex-wrap items-center gap-3 max-md:flex-col max-md:items-stretch">
        <PeriodPicker onKies={kies} waarde={week} weken={weken} werksoort={werksoort} />
        <Werksoortschakelaar week={week} />
      </div>
      <div
        className={`flex min-w-0 flex-col gap-5 rounded-xl ${FOCUSRING}`}
        data-testid="werkkwaliteit-team-inhoud"
        ref={inhoudRef}
        tabIndex={-1}
      >
        {inhoud()}
      </div>
    </div>
  );
}

const MELDING = 'rounded-xl border border-[var(--border-soft)] bg-[var(--background)] p-5 text-[0.8125rem] text-[var(--foreground)]';

/** Info-toestanden schrijven hun zin ook in het Voorleesgebied, zodat een week- of werksoortwissel ze laat horen. */
function OudeMeetmethode() {
  const { t } = useTranslation();
  const zin = t('qa.eigen.weekOudeMeetmethode');
  useMeldToestand(zin);
  return (
    <p className={MELDING} data-testid="werkkwaliteit-team-oude-meetmethode" role="status">
      {zin}
    </p>
  );
}

function NietsNagekeken({ week, werksoort }: { week: string; werksoort: Werksoort }) {
  const { t } = useTranslation();
  const kop = t('qa.team.nietsNagekeken', { week: weeknummer(week), werksoort: t(WERKSOORT_LABEL[werksoort]) });
  const uitleg = t('qa.team.nietsNagekekenUitleg', { andere: t(WERKSOORT_LABEL[ANDERE[werksoort]]) });
  useMeldToestand(`${kop} ${uitleg}`);
  return (
    <div className={MELDING} data-testid="werkkwaliteit-team-niets-nagekeken" role="status">
      <p className="font-semibold">{kop}</p>
      <p className="mt-1 text-[var(--foreground-muted)]">{uitleg}</p>
    </div>
  );
}

/** Laden: een skelet van de Teamzin en vijf rijen, nooit nullen als tussenstand. */
function TeamLaden() {
  const { t } = useTranslation();
  useMeldToestand(t('qa.eigen.laden'));
  return (
    <div aria-busy="true" className="flex flex-col gap-3" data-testid="werkkwaliteit-team-laden">
      <span className="sr-only">{t('qa.eigen.laden')}</span>
      <div aria-hidden="true" className="flex flex-col gap-3">
        <div className="h-12 rounded-xl bg-[var(--background-muted)]" data-werkkwaliteit-skelet="teamzin" />
        <div className="flex flex-col gap-2 rounded-xl border border-[var(--border-soft)] p-4">
          {[0, 1, 2, 3, 4].map((rij) => (
            <div className="h-11 rounded bg-[var(--background-muted)]" data-werkkwaliteit-skelet="rij" key={rij} />
          ))}
        </div>
      </div>
    </div>
  );
}
